import { FC, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCampaignsActions } from '../../../context';
import { CreateCampaignFormInput } from '../../../validation';
import { CampaignStepHeader, CampaignStepTitle } from '../../common';
import { BaseCampaignStep } from '../BaseCampaignStep';
import { AudiencesTable, AudienceTableSearch } from './AudiencesTable';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDeleteAudienceMutation } from '@/services/endpoints/audiences';
import { RootState, useAppDispatch } from '@/reducers';
import { useSelector } from 'react-redux';
import { useModalContext } from '@/hooks/useModalContext';
import { clearStashedAudience } from '@/features/audienceEdit/audienceEditSlice';
import { PlusIcon } from '@heroicons/react/outline';
import { AudienceEditV2 } from '@/features/audienceEdit_v2';

export const SelectAudienceStep: FC = () => {
  const { t } = useTranslation();
  const { setNextStep } = useCampaignsActions();
  const { watch } = useFormContext<CreateCampaignFormInput>();

  const audience = watch('audience');

  const workspace = useWorkspace();

  const [deleteAudience] = useDeleteAudienceMutation();
  const stashedAudience = useSelector((state: RootState) => state.audienceEdit.audience);
  const stashedAudienceRef = useRef(stashedAudience);

  useEffect(() => {
    stashedAudienceRef.current = stashedAudience;
  }, [stashedAudience]);

  const dispatch = useAppDispatch();
  const { openModal } = useModalContext();

  const handleModalClose = () => {
    const createdAudience = stashedAudienceRef.current;
    stashedAudienceRef.current = undefined;

    if (createdAudience && createdAudience.id && Object.keys(createdAudience.expression || {}).length === 0) {
      deleteAudience({
        workspaceId: workspace.id,
        audienceId: createdAudience.id,
      });
    }
  };

  const handleCreateAudience = () => {
    dispatch(clearStashedAudience());

    openModal({
      renderContent: AudienceEditV2,
      renderContentProps: {
        onSuccess: () => {},
      },
      fullHeight: true,
      fullWidth: true,
      onClose: handleModalClose,
    });
  };

  return (
    <BaseCampaignStep>
      <CampaignStepHeader
        classNames={{
          wrapper: 'mb-12',
        }}
        primaryButton={{
          text: t('COMMON:confirm'),
          onClick: setNextStep,
          disabled: !audience,
        }}
        secondaryButton={{
          text: t('audiences:buttons.add_audience'),
          onClick: handleCreateAudience,
          trailingIcon: <PlusIcon className='size-5' />,
        }}
      >
        <CampaignStepTitle title='selectAudience_title' description='selectAudience_description' />
      </CampaignStepHeader>
      <div className='grid gap-8'>
        <AudienceTableSearch />
        <AudiencesTable />
      </div>
    </BaseCampaignStep>
  );
};
